// src/components/common/taskbox/CreateTaskWidget.js
import React, { useState } from 'react';
import { Plus, X, ChevronDown, ChevronUp } from 'lucide-react';
import taskService from '../../../services/task.service';

const PRIORITY_OPTIONS = [
    { value: 'immediate', label: 'Immediate', color: 'red' },
    { value: 'urgent', label: 'Urgent', color: 'orange' },
    { value: 'next_in_line', label: 'Next in Line', color: 'yellow' },
    { value: 'scheduled', label: 'Scheduled', color: 'green' },
    { value: 'deferred', label: 'Deferred', color: 'gray' }
];

const TASK_TYPES = [
    { value: 'daily', label: 'Daily Task' },
    { value: 'weekly', label: 'Weekly Task' },
    { value: 'monthly', label: 'Monthly Task' },
    { value: 'quarterly', label: 'Quarterly Task' },
    { value: 'annual', label: 'Annual Task' }
];

const CATEGORIES = [
    { value: 'work', label: 'Work' },
    { value: 'personal', label: 'Personal' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'project', label: 'Project' },
    { value: 'documentation', label: 'Documentation' },
    { value: 'review', label: 'Review' },
    { value: 'other', label: 'Other' }
];

const CreateTaskWidget = ({ onTaskCreated, userRole, theme }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        task_type: 'daily',
        priority: 'scheduled',
        deadline: '',
        category: 'work'
    });
    const [checklistItems, setChecklistItems] = useState([]);
    const [newChecklistItem, setNewChecklistItem] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const addChecklistItem = () => {
        if (newChecklistItem.trim()) {
            setChecklistItems(prev => [...prev, {
                title: newChecklistItem.trim(),
                is_completed: false
            }]);
            setNewChecklistItem('');
        }
    };

    const removeChecklistItem = (index) => {
        setChecklistItems(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            alert('Please enter a task title');
            return;
        }

        setLoading(true);
        try {
            const taskData = {
                ...formData,
                // Convert empty strings to null for date fields
                deadline: formData.deadline || null,
                start_date: formData.start_date || null,
                due_date: formData.due_date || null,
                // Convert empty strings to null for text fields
                description: formData.description || null,
                category: formData.category || null,
                // Only include checklist if there are items
                checklist: checklistItems.length > 0 ? checklistItems : null
            };

            //console.log('📝 Creating task with data:', taskData);

            const response = await taskService.createTask(taskData);

            //console.log('✅ Task created successfully:', response);

            // Reset form
            setFormData({
                title: '',
                description: '',
                task_type: 'daily',
                priority: 'scheduled',
                deadline: '',
                category: 'work'
            });
            setChecklistItems([]);
            setIsExpanded(false);

            if (onTaskCreated) {
                onTaskCreated();
            }

            alert('Task created successfully!');
        } catch (error) {
            console.error('❌ Error creating task:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                url: error.config?.url,
                method: error.config?.method
            });

            const errorData = error.response?.data;
            let errorMessage = 'Unknown error';

            if (errorData) {
                errorMessage = errorData.message || errorData.error || 'Unknown error';

                // Add detailed error info if available
                if (errorData.errorCode) {
                    errorMessage += `\nError Code: ${errorData.errorCode}`;
                }
                if (errorData.errorDetail) {
                    errorMessage += `\nDetail: ${errorData.errorDetail}`;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            alert(`Failed to create task:\n\n${errorMessage}\n\nPlease check the browser console and backend logs for more details.`);
        } finally {
            setLoading(false);
        }
    };

    const getPriorityColorClass = (priority, isButton = false) => {
        const colors = {
            immediate: isButton ? 'border-red-600 text-red-600' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
            urgent: isButton ? 'border-orange-600 text-orange-600' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
            next_in_line: isButton ? 'border-yellow-600 text-yellow-600' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
            scheduled: isButton ? 'border-green-600 text-green-600' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
            deferred: isButton ? 'border-gray-600 text-gray-600' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
        };
        return colors[priority] || colors.scheduled;
    };

    return (
        <div className={`rounded-xl p-6 border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
            <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                    Create New Task
                </h3>
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                    {isExpanded ? (
                        <>
                            <ChevronUp className="w-4 h-4" />
                            Hide Form
                        </>
                    ) : (
                        <>
                            <Plus className="w-4 h-4" />
                            Create Task
                        </>
                    )}
                </button>
            </div>

            {isExpanded && (
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Title */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Task Title <span className="text-red-600">*</span>
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="Enter task title..."
                            required
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                            } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Description
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Add task description..."
                            rows="3"
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                            } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                        />
                    </div>

                    {/* Task Type and Category */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={`block text-sm font-medium mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                Task Type
                            </label>
                            <select
                                name="task_type"
                                value={formData.task_type}
                                onChange={handleChange}
                                className={`w-full px-4 py-2 rounded-lg border ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                            >
                                {TASK_TYPES.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className={`block text-sm font-medium mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                Category
                            </label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                className={`w-full px-4 py-2 rounded-lg border ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                            >
                                {CATEGORIES.map(cat => (
                                    <option key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Priority */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Priority
                        </label>
                        <div className="grid grid-cols-5 gap-2">
                            {PRIORITY_OPTIONS.map(priority => (
                                <button
                                    key={priority.value}
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, priority: priority.value }))}
                                    className={`px-3 py-2 rounded-lg border-2 font-medium text-sm transition-all ${
                                        formData.priority === priority.value
                                            ? getPriorityColorClass(priority.value, true) + ' bg-opacity-10'
                                            : theme === 'dark'
                                                ? 'border-gray-600 text-gray-400 hover:border-gray-500'
                                                : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                    }`}
                                >
                                    {priority.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Deadline */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Deadline
                        </label>
                        <input
                            type="date"
                            name="deadline"
                            value={formData.deadline}
                            onChange={handleChange}
                            min={new Date().toISOString().split('T')[0]}
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                        />
                    </div>

                    {/* Checklist */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Checklist Items
                        </label>
                        <div className="flex gap-2 mb-3">
                            <input
                                type="text"
                                value={newChecklistItem}
                                onChange={(e) => setNewChecklistItem(e.target.value)}
                                placeholder="Add checklist item..."
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addChecklistItem();
                                    }
                                }}
                                className={`flex-1 px-4 py-2 rounded-lg border ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                            />
                            <button
                                type="button"
                                onClick={addChecklistItem}
                                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                            >
                                Add
                            </button>
                        </div>

                        {checklistItems.length > 0 && (
                            <div className={`space-y-2 p-3 rounded-lg ${
                                theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                            }`}>
                                {checklistItems.map((item, index) => (
                                    <div key={index} className={`flex items-center gap-3 p-2 rounded ${
                                        theme === 'dark' ? 'bg-gray-700' : 'bg-white'
                                    }`}>
                                        <input type="checkbox" disabled className="w-4 h-4" />
                                        <span className={`flex-1 text-sm ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            {item.title}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => removeChecklistItem(index)}
                                            className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex gap-3 pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}">
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Creating...' : 'Create Task'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsExpanded(false)}
                            disabled={loading}
                            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                                    : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default CreateTaskWidget;
